import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import CloseButton from './CloseButton';
import PublicAccessPermissionIcons from './PublicAccessPermissionIcons';
import { useTranslation } from 'react-i18next';
import {
  getEffectiveProjectAccessFlags,
  getEffectiveProjectAccessLevel
} from '../../Utils/permissionScope';

const normalizePublicAccessLevel = (value) => {
  const normalized = String(value || 'view').trim().toLowerCase();
  return ['view', 'upload', 'view_upload'].includes(normalized) ? normalized : 'view';
};
const publicAccessFlagsFromLevel = (value) => {
  const normalized = normalizePublicAccessLevel(value);
  return {
    canView: normalized === 'view' || normalized === 'view_upload',
    canUpload: normalized === 'upload' || normalized === 'view_upload'
  };
};
const publicAccessLevelFromFlags = ({ canView, canUpload }) => {
  if (canView && canUpload) return 'view_upload';
  if (canUpload) return 'upload';
  return 'view';
};

const ProjectShareDialog = ({
  open,
  onClose,
  users = [],
  currentUsername = '',
  projectId,
  projectTitle = '',
  onApplyAccess,
  savingUsername = '',
  loading = false,
  isPublic = false,
  isPublicLoading = false,
  publicAccessLevel = 'view',
  onTogglePublic
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [draftAccessByUsername, setDraftAccessByUsername] = useState({});
  const [publicWarningOpen, setPublicWarningOpen] = useState(false);
  const [draftPublicAccessFlags, setDraftPublicAccessFlags] = useState(publicAccessFlagsFromLevel(publicAccessLevel));

  const publicUrl = useMemo(() => {
    if (typeof window === 'undefined' || !projectId) {
      return '';
    }
    return `${window.location.origin}/public/case/${projectId}`;
  }, [projectId]);

  useEffect(() => {
    setDraftPublicAccessFlags(publicAccessFlagsFromLevel(publicAccessLevel));
  }, [publicAccessLevel]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      return;
    }
    const nextDrafts = {};
    users.forEach((userRow) => {
      const username = String(userRow?.username || '').trim();
      if (!username) return;
      nextDrafts[username] = getEffectiveProjectAccessFlags(userRow, projectId);
    });
    setDraftAccessByUsername(nextDrafts);
  }, [open, users, projectId]);

  const getAccessLabel = (accessLevel) => {
    if (accessLevel === 'admin') {
      return t('account_management.admin');
    }
    if (accessLevel === 'editor') {
      return t('account_management.editor');
    }
    if (accessLevel === 'edit') {
      return t('project_share.edit', 'Edit');
    }
    if (accessLevel === 'upload') {
      return t('project_share.upload_only', 'Upload only');
    }
    if (accessLevel === 'read') {
      return t('project_share.view_only', 'View only');
    }
    if (accessLevel === 'none') {
      return t('project_share.no_access', 'No access');
    }
    return accessLevel;
  };

  const handleUserAccessToggle = (username, mode) => () => {
    setDraftAccessByUsername((prev) => {
      const current = prev[username] || { read: false, upload: false, edit: false };
      const next = {
        ...current,
        [mode]: !current[mode]
      };

      if (mode === 'edit' && next.edit) {
        next.read = true;
        next.upload = true;
      }
      if (mode === 'upload' && !next.upload) {
        next.edit = false;
      }

      return {
        ...prev,
        [username]: next
      };
    });
  };

  const visibleUsers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return users
      .filter((userRow) => {
        const username = String(userRow?.username || '').trim();
        return username && username !== currentUsername && !userRow?.is_system_user && username !== 'public';
      })
      .filter((userRow) => {
        const currentAccess = getEffectiveProjectAccessLevel(userRow, projectId);
        if (!normalizedQuery) {
          return currentAccess !== 'none';
        }
        return true;
      })
      .filter((userRow) => {
        if (!normalizedQuery) return true;
        const fullName = [userRow?.first_name, userRow?.last_name].filter(Boolean).join(' ');
        const haystack = [userRow?.username, userRow?.email, fullName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      })
      .sort((left, right) => {
        const leftAccess = getEffectiveProjectAccessLevel(left, projectId);
        const rightAccess = getEffectiveProjectAccessLevel(right, projectId);
        const leftHasAccess = leftAccess !== 'none';
        const rightHasAccess = rightAccess !== 'none';
        if (leftHasAccess !== rightHasAccess) {
          return leftHasAccess ? -1 : 1;
        }
        return String(left?.username || '').localeCompare(String(right?.username || ''));
      });
  }, [users, currentUsername, searchQuery, projectId]);

  const handlePublicToggle = (event) => {
    const nextValue = event.target.checked;
    if (nextValue) {
      setPublicWarningOpen(true);
      return;
    }
    if (onTogglePublic) {
      onTogglePublic(false, publicAccessLevelFromFlags(draftPublicAccessFlags));
    }
  };

  const handleConfirmPublicToggle = () => {
    setPublicWarningOpen(false);
    if (onTogglePublic) {
      onTogglePublic(true, publicAccessLevelFromFlags(draftPublicAccessFlags));
    }
  };

  const handlePublicAccessToggle = (mode) => () => {
    setDraftPublicAccessFlags((prev) => {
      const next = {
        ...prev,
        [mode]: !prev[mode]
      };
      if (!next.canView && !next.canUpload) {
        return prev;
      }
      if (isPublic && onTogglePublic) {
        onTogglePublic(true, publicAccessLevelFromFlags(next));
      }
      return next;
    });
  };

  const handleCopyPublicUrl = async () => {
    if (!publicUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return;
    }
    await navigator.clipboard.writeText(publicUrl);
  };

  return (
    <Dialog
      open={open}
      onClose={savingUsername ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '14px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #5757b0, #7777d0)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ShareIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('project_share.title', 'Share Project')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.92 }}>
              {t('project_share.subtitle', 'Grant per-project access using the same scoped permissions as account management')}
            </Typography>
          </Box>
        </Stack>
        <CloseButton
          onClick={onClose}
          sx={{
            color: '#fff',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' }
          }}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.9) 0%, rgba(255, 255, 255, 1) 100%)'
        }}
      >
        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '12px',
              p: 1.5,
              borderColor: 'rgba(87, 87, 176, 0.18)',
              backgroundColor: 'rgba(255,255,255,0.78)'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {projectTitle || t('sidebar.untitled_case', 'Untitled project')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('project_share.project_id', 'Project #{{projectId}}', { projectId })}
            </Typography>
          </Paper>

          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('project_share.search_user', 'Search by username, name, or email')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
            {searchQuery.trim()
              ? t('project_share.search_hint', 'Search for a user to grant or change access.')
              : t('project_share.default_list_hint', 'Showing only users who already have access to this project.')}
          </Typography>

          <List
            dense
            disablePadding
            sx={{
              border: '1px solid rgba(87, 87, 176, 0.15)',
              borderRadius: '12px',
              backgroundColor: '#fff',
              maxHeight: '52vh',
              overflowY: 'auto'
            }}
          >
            {visibleUsers.length === 0 && (
              <ListItem sx={{ py: 4, justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {loading
                    ? t('project_share.loading_users', 'Loading users...')
                    : searchQuery.trim()
                      ? t('project_share.no_users_found', 'No matching users found')
                      : t('project_share.no_shared_users', 'No users currently have direct access to this project')}
                </Typography>
              </ListItem>
            )}
            {visibleUsers.map((userRow) => {
              const username = String(userRow?.username || '').trim();
              const currentAccess = getEffectiveProjectAccessLevel(userRow, projectId);
              const currentAccessFlags = getEffectiveProjectAccessFlags(userRow, projectId);
              const selectedAccessFlags = draftAccessByUsername[username] || currentAccessFlags;
              const isGlobalAccess = currentAccess === 'admin' || currentAccess === 'editor';
              const isSaving = savingUsername === username;
              const fullName = [userRow?.first_name, userRow?.last_name].filter(Boolean).join(' ');
              const hasChanges = (
                selectedAccessFlags.read !== currentAccessFlags.read ||
                selectedAccessFlags.upload !== currentAccessFlags.upload ||
                selectedAccessFlags.edit !== currentAccessFlags.edit
              );
              const currentAccessLabel = isGlobalAccess
                ? getAccessLabel(currentAccess)
                : currentAccessFlags.edit
                  ? t('project_share.edit', 'Edit')
                  : (currentAccessFlags.read && currentAccessFlags.upload)
                    ? `${t('project_share.view_only', 'View only')} + ${t('project_share.upload_only', 'Upload only')}`
                    : currentAccessFlags.upload
                      ? t('project_share.upload_only', 'Upload only')
                      : currentAccessFlags.read
                        ? t('project_share.view_only', 'View only')
                        : t('project_share.no_access', 'No access');
              const currentAccessColor = isGlobalAccess || currentAccessFlags.edit
                ? 'success'
                : (currentAccessFlags.read && currentAccessFlags.upload)
                  ? 'secondary'
                  : currentAccessFlags.upload
                    ? 'warning'
                    : currentAccessFlags.read
                      ? 'info'
                      : 'default';

              const permissionIconButton = (mode, icon, tooltip) => {
                const checked = Boolean(selectedAccessFlags[mode]);
                return (
                  <Tooltip key={`${username}-${mode}`} title={tooltip} arrow enterDelay={180}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={isGlobalAccess || isSaving}
                        onClick={handleUserAccessToggle(username, mode)}
                        aria-pressed={checked}
                        sx={{
                          width: 34,
                          height: 34,
                          border: '1px solid',
                          borderColor: checked ? 'rgba(87, 87, 176, 0.55)' : 'rgba(148, 163, 184, 0.35)',
                          backgroundColor: checked ? 'rgba(87, 87, 176, 0.12)' : 'transparent',
                          color: checked ? '#373f8c' : 'rgba(71, 85, 105, 0.8)',
                          '&:hover': {
                            backgroundColor: checked ? 'rgba(87, 87, 176, 0.18)' : 'rgba(148, 163, 184, 0.08)'
                          }
                        }}
                      >
                        {icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              };

              return (
                <ListItem
                  key={username}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 150px 130px' },
                    gap: 1.5,
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid rgba(87, 87, 176, 0.08)',
                    '&:last-of-type': { borderBottom: 'none' }
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {fullName || username}
                      </Typography>
                      <Chip
                        size="small"
                        label={currentAccessLabel}
                        color={currentAccessColor}
                        variant={currentAccessColor === 'default' ? 'outlined' : 'filled'}
                      />
                      {isGlobalAccess && (
                        <Tooltip title={t('project_share.global_access_tooltip', 'This user already has global access and does not need project-specific sharing')} arrow>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('project_share.global_access', 'Global access')}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      @{username}{userRow?.email ? ` - ${userRow.email}` : ''}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'center' }}>
                    {permissionIconButton('read', <VisibilityIcon fontSize="small" />, t('project_share.view_only', 'View only'))}
                    {permissionIconButton('upload', <UploadFileIcon fontSize="small" />, t('project_share.upload_only', 'Upload only'))}
                    {permissionIconButton('edit', <EditIcon fontSize="small" />, t('project_share.edit', 'Edit'))}
                  </Stack>

                  <Button
                    variant="contained"
                    disabled={isGlobalAccess || isSaving || !hasChanges}
                    onClick={() => onApplyAccess && onApplyAccess(userRow, selectedAccessFlags)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      backgroundColor: '#5757b0',
                      '&:hover': {
                        backgroundColor: '#4b4ba2'
                      }
                    }}
                  >
                    {isSaving
                      ? t('project_share.applying', 'Applying...')
                      : !selectedAccessFlags.read && !selectedAccessFlags.upload && !selectedAccessFlags.edit
                        ? t('project_share.remove_access', 'Remove Access')
                        : t('project_share.apply_access', 'Apply Access')}
                  </Button>
                </ListItem>
              );
            })}
          </List>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: '12px',
              p: 1.5,
              borderColor: 'rgba(87, 87, 176, 0.18)',
              backgroundColor: 'rgba(255,255,255,0.78)'
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="center">
                <PublicIcon sx={{ color: '#5757b0' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('settings.others.public_access', 'Public Access')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('settings.others.public_access_tooltip', 'Make this project publicly accessible via a shared link')}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PublicAccessPermissionIcons
                  accessFlags={draftPublicAccessFlags}
                  disabled={isPublicLoading}
                  onToggleView={handlePublicAccessToggle('canView')}
                  onToggleUpload={handlePublicAccessToggle('canUpload')}
                />
                <Switch
                  checked={isPublic}
                  onChange={handlePublicToggle}
                  disabled={isPublicLoading}
                  color="primary"
                />
              </Stack>
            </Stack>
            {isPublic && (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('settings.others.public_link_info', 'This project is currently public.')}
                </Alert>
                {publicUrl && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={publicUrl}
                      InputProps={{ readOnly: true }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCopyPublicUrl}
                      sx={{ textTransform: 'none', borderRadius: '10px', whiteSpace: 'nowrap' }}
                    >
                      {t('project_share.copy_public_link', 'Copy Link')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={Boolean(savingUsername)}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            borderColor: 'rgba(87, 87, 176, 0.3)',
            color: '#5757b0'
          }}
        >
          {t('project_share.close', 'Close')}
        </Button>
      </DialogActions>

      <Dialog
        open={publicWarningOpen}
        onClose={() => setPublicWarningOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: '#ff9800', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon fontSize="small" />
          {t('settings.others.public_warning_title', 'Make Project Public?')}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Typography variant="body2">
            {t('settings.others.public_warning_text', 'Making this project public will allow anyone with the link to view it. Are you sure you want to proceed?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPublicWarningOpen(false)} variant="outlined" color="inherit" sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmPublicToggle}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProjectShareDialog;
